import { Sparkles, AlertTriangle } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

export const AI_LABELS = {
  load_number: "Broker ref #",
  broker_name: "Broker",
  rate: "Rate",
  pickup_location: "Pickup",
  delivery_location: "Delivery",
  pickup_date: "Pickup date",
  delivery_date: "Delivery date",
  pickup_appt: "Pickup appointment",
  delivery_appt: "Delivery appointment",
};

// Overlay extracted values on a form. Confident values replace; unreadable fields keep what's there and get flagged.
export function applyExtraction(form, result) {
  const next = { ...form };
  const marks = {};
  Object.keys(AI_LABELS).forEach((k) => {
    const v = result.fields[k];
    if (v !== null && v !== undefined && !result.flagged.includes(k)) {
      next[k] = k === "rate" ? String(v) : v;
      marks[k] = "filled";
    } else {
      marks[k] = String(form[k] ?? "").trim() ? "kept" : "flagged";
    }
  });
  return { form: next, marks };
}

export const FieldLabel = ({ htmlFor, children, mark, testId }) => (
  <div className="flex items-center justify-between gap-2">
    <Label htmlFor={htmlFor}>{children}</Label>
    {mark === "filled" && (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-[#002FA7] bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5" data-testid={`${testId}-ai-filled`}>
        <Sparkles className="w-3 h-3" /> AI-filled
      </span>
    )}
    {mark === "flagged" && (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" data-testid={`${testId}-ai-flagged`}>
        <AlertTriangle className="w-3 h-3" /> Couldn&apos;t read, please fill
      </span>
    )}
    {mark === "kept" && (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-600 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5" data-testid={`${testId}-ai-kept`}>
        Couldn&apos;t read, kept current value
      </span>
    )}
  </div>
);

export const AiBanner = ({ marks, notes }) => {
  const flagged = Object.entries(marks).filter(([, m]) => m === "flagged").map(([k]) => AI_LABELS[k]);
  return (
    <div className="rounded-xl border border-blue-200 bg-blue-50/70 p-4 text-sm" data-testid="ai-verify-banner">
      <div className="flex items-center gap-2 font-semibold text-[#002FA7]"><Sparkles className="w-4 h-4" /> AI-filled, please verify</div>
      <p className="mt-1 text-slate-700">Check every field against the ratecon before saving. AI can make mistakes.</p>
      {flagged.length > 0 && (
        <p className="mt-1 text-amber-800" data-testid="ai-flagged-list">Couldn&apos;t read: {flagged.join(", ")}. These were left for you to fill in.</p>
      )}
      {notes && <p className="mt-1 text-slate-600 italic" data-testid="ai-notes">Note: {notes}</p>}
    </div>
  );
};

export const AiConfirm = ({ checked, onChange }) => (
  <label className="flex items-start gap-2.5 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm cursor-pointer" data-testid="ai-confirm">
    <Checkbox checked={checked} onCheckedChange={(v) => onChange(!!v)} className="mt-0.5" data-testid="ai-confirm-checkbox" />
    <span>I&apos;ve checked the auto-filled details against the ratecon.</span>
  </label>
);
